/**
 * Digital creatives — ADMIN DATA-CLEANING cockpit. Three levels:
 *   advertiser batch (AR, named by observed_ads.advertiser_name)
 *     → sub-groups of similar creatives (by domain / normalized vision brand)
 *       → creative (click image = enlarged lightbox)
 * Resolve a whole batch / a sub-group / a single creative to one brand, or skip.
 * No vanity stats — the admin cleans data here.
 */
import { useState } from 'react';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton, SectionErrorBoundary } from '@shared/ui';
import { useDebounce } from '@shared/hooks/useDebounce';
import { captureError } from '@shared/lib/observability';

interface Batch {
  source: string; advertiser_ext_id: string; advertiser_name: string | null;
  creatives: number; vision_done: number; high_conf: number; distinct_brands: number;
  top_brand: string | null; sample_domain: string | null; ext_ad_ids: string[];
  advertiser_kind: string | null;
}
interface Creative {
  ext_ad_id: string; visible_brand: string | null; display_domain: string | null;
  brand_confidence: string | null; format: string | null; ad_text: string | null;
  image_url: string | null; landing_url: string | null;
}
const fmtIcon = (f: string | null) => f === 'video' ? '🎬' : f === 'image' ? '🖼' : f === 'text' ? '📄' : '·';
// A content.js URL is a JS ad-preview, NOT an image — never feed it to <img>/background
// (renders blank/broken). Until it's mirrored, show the format placeholder instead.
const imgOf = (u: string | null) => (u && !u.includes('content.js') ? u : null);
type Hit = { brand_id: string; brand_name: string; matched_via: string };
type Subcat = { id: string; name: string };
type CreateTarget = { ids: string[]; name: string; domain: string | null };
type DomainCand = {
  domain: string; unmatched_creatives: number; site_title: string | null;
  instagram: string | null; whatsapp: string | null; phones: string | null;
  twogis_firm_id: string | null; status: string | null; mention_count: number | null;
  canonical_suggestion: string | null; ext_ad_ids: string[];
};

const bareHost = (d: string | null) =>
  (d || '').toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/.*$/, '').trim();
const normBrand = (b: string | null) => (b || '').toLowerCase().replace(/[^a-zа-я0-9]+/gi, '').trim();
// A visible_brand like "Kaspi.kz" is really a domain — fold it into the domain key
// so it doesn't split from the "kaspi.kz" display_domain group.
const looksLikeDomain = (s: string | null) => !!s && /^[^\s]+\.[a-z]{2,}$/i.test(s.trim());
// A real domain host MUST contain a dot — vision sometimes emits a bare word
// like "Port" as display_domain, which must NOT become its own domain group.
const NO_SIGNAL = 'x:__nosignal';
function subKey(c: Creative) {
  const h = bareHost(c.display_domain);
  if (h && h.includes('.')) return 'd:' + h;
  if (looksLikeDomain(c.visible_brand)) return 'd:' + bareHost(c.visible_brand);
  const b = normBrand(c.visible_brand); if (b) return 'b:' + b;
  // No usable signal → ALL such creatives collapse into ONE group (not one row each),
  // so the admin attributes them together instead of clicking 12 identical tiles.
  return NO_SIGNAL;
}
const mode = (arr: string[]): string | null => {
  if (!arr.length) return null;
  const c = new Map<string, number>(); let best = arr[0], bn = 0;
  for (const x of arr) { const n = (c.get(x) ?? 0) + 1; c.set(x, n); if (n > bn) { bn = n; best = x; } }
  return best;
};
const chip = (txt: string, color: string) => (
  <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 'var(--fs-micro)', fontWeight: 600,
    background: `${color}22`, color, border: `1px solid ${color}66` }}>{txt}</span>
);
const btn = (bg: string, color: string, busy: boolean): React.CSSProperties => ({
  padding: '5px 10px', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
  fontSize: 'var(--fs-small)', fontWeight: 600, background: bg, color,
  border: bg === 'transparent' ? '1px solid rgba(255,255,255,0.15)' : 'none',
});

export default function DigitalAdsReview() {
  const qc = useQueryClient();
  const batchesQ = useSupabaseQuery<Batch[]>(['digital-ad-batches'],
    async () => supabase.rpc('get_digital_ad_batches', { p_limit: 300 }));
  const subcatsQ = useSupabaseQuery<Subcat[]>(['subcategories-all'],
    async () => supabase.from('subcategories').select('id, name').order('name'));
  const domainsQ = useSupabaseQuery<DomainCand[]>(['digital-domain-candidates'],
    async () => supabase.rpc('get_digital_domain_candidates', { p_limit: 200 }));
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['digital-ad-batches'] });
    qc.invalidateQueries({ queryKey: ['digital-batch-creatives'] });
    qc.invalidateQueries({ queryKey: ['digital-domain-candidates'] });
  };
  const batches = batchesQ.data ?? [];
  return (
    <SectionErrorBoundary name="digital-clean">
      <p style={{ color: 'var(--muted)', fontSize: 'var(--fs-body)', margin: '0 0 14px' }}>
        Непривязанные digital-рекламодатели (AR). Клик — раскрыть креативы (сгруппированы по похожести). Обычный
        рекламодатель → привязать батч к одному бренду; агентство → по подгруппам/креативам. Клик по картинке — увеличить.
      </p>
      <DomainCandidates rows={domainsQ.data ?? []} subcats={subcatsQ.data ?? []} onChange={invalidate} />
      {batchesQ.isLoading && <Skeleton height={300} />}
      {!batchesQ.isLoading && batches.length === 0 && <div className="admin-empty">Очередь чистая.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {batches.map((b) => <BatchRow key={b.source + ':' + b.advertiser_ext_id} b={b} subcats={subcatsQ.data ?? []} onChange={invalidate} />)}
      </div>
    </SectionErrorBoundary>
  );
}

// Domain worklist: unresolved domains (from ad_domains) that still have unmatched creatives,
// with discovered contacts pre-filled. One click → create a brand (website = domain) or link an
// existing one, attributing every creative on that domain. Surfaced instead of a bulk auto-create.
function DomainCandidates({ rows, subcats, onChange }: { rows: DomainCand[]; subcats: Subcat[]; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createT, setCreateT] = useState<CreateTarget | null>(null);
  const [subId, setSubId] = useState('');
  const [mTerm, setMTerm] = useState('');
  const mDeb = useDebounce(mTerm, 250);
  const searchQ = useSupabaseQuery<Hit[]>(['dom-brand-search', mDeb], async () => {
    const q = mDeb.trim();
    if (q.length < 2) return { data: [], error: null };
    return supabase.rpc('find_brands_by_search', { p_term: q, p_limit: 20 });
  }, { enabled: !!createT && mDeb.trim().length >= 2 });
  const call = async (args: Record<string, unknown>) => {
    setBusy(true);
    const { error } = await supabase.rpc('admin_resolve_digital_cluster', args);
    setBusy(false);
    if (error) { captureError(error, { where: 'digitalClean.domain' }); alert(error.message); return; }
    setCreateT(null); setSubId(''); setMTerm(''); onChange();
  };
  // vision site_title is noisy (mojibake, "404", "Главная") → use first clean segment, else the domain.
  const cleanName = (d: DomainCand) => {
    const t = (d.site_title || '').split(/[|–—\-–]/)[0].trim();
    const bad = /^(\d{3}\b|404|not found|error|главная|home|страница не найдена|page not found)/i;
    return (t.length >= 2 && t.length <= 60 && !bad.test(t)) ? t : d.domain;
  };
  const openCreate = (d: DomainCand) => {
    const nm = cleanName(d); setCreateT({ ids: d.ext_ad_ids, name: nm, domain: d.domain }); setSubId(''); setMTerm(nm);
  };
  const doCreate = () => {
    if (!createT || !createT.name.trim()) return;
    call({ p_source: 'google', p_ext_ad_ids: createT.ids, p_brand_id: null, p_new_brand_name: createT.name.trim(), p_new_website: createT.domain, p_subcategory_id: subId || null });
  };
  const link = (brandId: string) => createT && call({ p_source: 'google', p_ext_ad_ids: createT.ids, p_brand_id: brandId });
  const resolveSug = async (domain: string, canon: string, apply: boolean) => {
    setBusy(true);
    const { error } = await supabase.rpc('admin_resolve_domain_suggestion', { p_domain: domain, p_canonical: canon, p_apply: apply });
    setBusy(false);
    if (error) { captureError(error, { where: 'digitalClean.domainSuggestion' }); alert(error.message); return; }
    onChange();
  };

  if (!rows.length) return null;
  const totalCr = rows.reduce((s, r) => s + r.unmatched_creatives, 0);
  return (
    <div style={{ border: '1px solid rgba(240,160,32,0.35)', borderRadius: 8, padding: 12, marginBottom: 14, background: 'rgba(240,160,32,0.04)', opacity: busy ? 0.5 : 1 }}>
      <button onClick={() => setOpen(!open)} style={{ ...btn('transparent', '#f0c060', busy), fontSize: 'var(--fs-body)' }}>
        {open ? '▾' : '▸'} 🌐 Домены без бренда — {rows.length} доменов · {totalCr} креативов
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--muted)' }}>
            Домен = сильный сигнал бренда (по vision + скрап контактов). Создай/привяжи — привяжет все креативы этого домена разом.
          </div>
          {rows.map((d) => (
            <div key={d.domain} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '5px 0' }}>
              <b style={{ fontSize: 'var(--fs-small)' }}>{d.domain}</b>
              {chip(`${d.unmatched_creatives} креат.`, '#4a9eff')}
              {!!d.mention_count && chip(`★ упомянут ×${d.mention_count}`, '#f5c518')}
              {d.canonical_suggestion && (
                <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
                  <button disabled={busy} title="это опечатка — привязать к этому домену"
                    onClick={() => resolveSug(d.domain, d.canonical_suggestion!, true)}
                    style={btn('rgba(192,132,252,0.18)', '#c084fc', busy)}>≈ {d.canonical_suggestion} ✓</button>
                  <button disabled={busy} title="не опечатка — убрать подсказку"
                    onClick={() => resolveSug(d.domain, d.canonical_suggestion!, false)}
                    style={btn('transparent', '#888', busy)}>✕</button>
                </span>
              )}
              {d.status === 'down' && chip('сайт недоступен', '#888')}
              {d.site_title && <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-micro)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.site_title}</span>}
              {d.instagram && chip(`ig:${d.instagram}`, '#e1306c')}
              {d.whatsapp && chip('wa', '#25d366')}
              {d.phones && <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-micro)' }}>{d.phones.split('|')[0]}</span>}
              <div style={{ flex: 1 }} />
              <button disabled={busy} onClick={() => openCreate(d)} style={btn('rgba(46,204,113,0.2)', '#7ee2a8', busy)}>+ Бренд / привязать</button>
            </div>
          ))}
        </div>
      )}

      {createT && (
        <div onClick={() => !busy && setCreateT(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 61, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: '100vw', maxHeight: '90vh', overflow: 'auto', background: '#0a1226', border: '1px solid rgba(15,45,100,0.6)', borderRadius: 10, padding: 18 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Привязать {createT.ids.length} креатив. · {createT.domain}</div>
            <label style={{ fontSize: 'var(--fs-small)', opacity: 0.7 }}>Найти существующий бренд</label>
            <input autoFocus value={mTerm} onChange={(e) => setMTerm(e.target.value)} placeholder="поиск бренда (мин. 2)…"
              style={{ width: '100%', margin: '4px 0 4px', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)' }} />
            {mDeb.trim().length >= 2 && (
              <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6, marginBottom: 10 }}>
                {(searchQ.data ?? []).length === 0 && <div style={{ padding: '6px 10px', fontSize: 'var(--fs-micro)', opacity: 0.5 }}>ничего — создайте новый ниже</div>}
                {(searchQ.data ?? []).map((h) => (
                  <div key={h.brand_id} onClick={() => !busy && link(h.brand_id)} style={{ padding: '7px 10px', cursor: busy ? 'wait' : 'pointer', fontSize: 'var(--fs-small)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    → {h.brand_name} <span style={{ opacity: 0.5 }}>({h.matched_via})</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0', opacity: 0.5, fontSize: 'var(--fs-micro)' }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.12)' }} /> или создать новый <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.12)' }} />
            </div>
            <label style={{ fontSize: 'var(--fs-small)', opacity: 0.7 }}>Название бренда</label>
            <input value={createT.name} onChange={(e) => setCreateT({ ...createT, name: e.target.value })}
              style={{ width: '100%', margin: '4px 0 8px', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)' }} />
            <div style={{ fontSize: 'var(--fs-small)', opacity: 0.6, marginBottom: 12 }}>сайт: {createT.domain || '—'}</div>
            <label style={{ fontSize: 'var(--fs-small)', opacity: 0.7 }}>Подкатегория</label>
            <select value={subId} onChange={(e) => setSubId(e.target.value)}
              style={{ width: '100%', margin: '4px 0 6px', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)' }}>
              <option value="">— без подкатегории (на ревью) —</option>
              {subcats.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button disabled={busy} onClick={() => setCreateT(null)} style={btn('transparent', '#9aa', busy)}>Отмена</button>
              <button disabled={busy || !createT.name.trim()} onClick={doCreate} style={btn('#2ecc71', '#062', busy)}>Создать и привязать</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BatchRow({ b, subcats, onChange }: { b: Batch; subcats: Subcat[]; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const [term, setTerm] = useState('');
  const [zoom, setZoom] = useState<Creative | null>(null);
  const [createT, setCreateT] = useState<CreateTarget | null>(null);
  const [subId, setSubId] = useState('');
  const [mTerm, setMTerm] = useState('');           // brand search INSIDE the attribute modal
  const debounced = useDebounce(term, 250);
  const mDebounced = useDebounce(mTerm, 250);

  const creativesQ = useSupabaseQuery<Creative[]>(
    ['digital-batch-creatives', b.source, b.advertiser_ext_id],
    async () => supabase.rpc('get_digital_ad_batch_creatives', { p_source: b.source, p_advertiser_ext_id: b.advertiser_ext_id }),
    { enabled: open },
  );
  const searchQ = useSupabaseQuery<Hit[]>(['digital-brand-search', debounced], async () => {
    const q = debounced.trim();
    if (q.length < 2) return { data: [], error: null };
    return supabase.rpc('find_brands_by_search', { p_term: q, p_limit: 20 });
  }, { enabled: search && debounced.trim().length >= 2 });
  const mSearchQ = useSupabaseQuery<Hit[]>(['digital-brand-search-modal', mDebounced], async () => {
    const q = mDebounced.trim();
    if (q.length < 2) return { data: [], error: null };
    return supabase.rpc('find_brands_by_search', { p_term: q, p_limit: 20 });
  }, { enabled: !!createT && mDebounced.trim().length >= 2 });

  const call = async (fn: string, args: Record<string, unknown>) => {
    setBusy(true);
    const { error } = await supabase.rpc(fn, args);
    setBusy(false);
    if (error) { captureError(error, { where: `digitalClean.${fn}` }); alert(error.message); return; }
    setSearch(false); setTerm(''); setCreateT(null); setSubId(''); setMTerm(''); onChange();
  };
  // Attribute modal: search + link to an EXISTING brand, or create a new one.
  // Opens pre-seeded with the vision brand name so the search is immediately populated.
  const openCreate = (ids: string[], name: string | null, domain: string | null) => {
    const d = domain || (looksLikeDomain(name) ? bareHost(name) : null);  // domain-shaped name → website
    setCreateT({ ids, name: name ?? '', domain: d }); setSubId(''); setMTerm((name ?? '').trim());
  };
  const doCreate = () => {
    if (!createT || !createT.name.trim()) return;
    call('admin_resolve_digital_cluster', { p_source: b.source, p_ext_ad_ids: createT.ids, p_brand_id: null, p_new_brand_name: createT.name.trim(), p_new_website: createT.domain, p_subcategory_id: subId || null });
  };
  const link = (ids: string[], brandId: string) =>
    call('admin_resolve_digital_cluster', { p_source: b.source, p_ext_ad_ids: ids, p_brand_id: brandId });
  const skip = (ids: string[]) => call('admin_skip_digital_cluster', { p_source: b.source, p_ext_ad_ids: ids });
  const setKind = (kind: string) =>
    call('admin_set_advertiser_kind', { p_source: b.source, p_advertiser_ext_id: b.advertiser_ext_id, p_kind: kind });

  const multi = b.distinct_brands > 1;
  const isAgency = b.advertiser_kind === 'agency' || b.advertiser_kind === 'adtech';
  // Sub-group the batch's creatives by domain / normalized brand.
  const subgroups = (() => {
    const m = new Map<string, Creative[]>();
    for (const c of creativesQ.data ?? []) { const k = subKey(c); (m.get(k) ?? m.set(k, []).get(k)!).push(c); }
    return [...m.entries()].map(([k, cs]) => {
      const domain = k.startsWith('d:') ? k.slice(2) : null;
      const brand = mode(cs.map((c) => (c.visible_brand || '').trim()).filter(Boolean)) ?? null;
      const label = k === NO_SIGNAL ? 'без vision-сигнала' : (domain || brand || '—');
      const suggest = domain || brand;  // seed for the attribute modal (search + create)
      return { key: k, label, brand: suggest, domain, cs, ids: cs.map((c) => c.ext_ad_id) };
    }).sort((a, z) =>
      a.key === NO_SIGNAL ? 1 : z.key === NO_SIGNAL ? -1 : z.cs.length - a.cs.length);
  })();

  return (
    <div style={{ border: '1px solid rgba(15,45,100,0.4)', borderRadius: 8, padding: 12, background: 'rgba(255,255,255,0.02)', opacity: busy ? 0.5 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {chip(b.source, '#888')}
        <b style={{ fontSize: 'var(--fs-body)' }}>{b.advertiser_name || b.advertiser_ext_id}</b>
        <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-small)' }}>{b.creatives} креатив. · {b.high_conf} high-conf</span>
        {multi && !isAgency && chip(`${b.distinct_brands} брендов — агентство?`, '#a78bfa')}
        {isAgency && chip(`⚑ ${b.advertiser_kind}`, '#f0a020')}
        {b.vision_done === 0 && chip('нужен vision', '#e74c3c')}
      </div>
      {b.top_brand && (
        <div style={{ fontSize: 'var(--fs-small)', color: '#cbd5ff', margin: '6px 0' }}>
          vision: <b>{b.top_brand}</b>{b.sample_domain ? ` · ${b.sample_domain}` : ''}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
        {!multi && b.top_brand && (
          <button disabled={busy} onClick={() => openCreate(b.ext_ad_ids, b.top_brand, b.sample_domain)} style={btn('#2ecc71', '#062', busy)}>
            + Бренд «{b.top_brand}» → привязать {b.creatives}
          </button>
        )}
        <button disabled={busy} onClick={() => { setSearch(!search); setTerm(''); }} style={btn('rgba(74,158,255,0.15)', '#bcd0ff', busy)}>
          {multi ? 'Весь батч → бренд…' : 'Другой бренд…'}
        </button>
        <button disabled={busy} onClick={() => skip(b.ext_ad_ids)} style={btn('transparent', '#9aa', busy)}>Не бренд (весь)</button>
        {isAgency
          ? <button disabled={busy} onClick={() => setKind('brand')} title="вернуть в бренды" style={btn('transparent', '#f0a020', busy)}>↩ в бренд</button>
          : <button disabled={busy} onClick={() => setKind('agency')} title="этот AR рекламирует много чужих брендов — пометить как агентство (по-креативная атрибуция)" style={btn(multi ? 'rgba(240,160,32,0.18)' : 'transparent', '#f0a020', busy)}>⚑ Агентство</button>}
        <button disabled={busy} onClick={() => setOpen(!open)} style={btn('transparent', '#bcd0ff', busy)}>
          {open ? '▾ Скрыть' : `▸ Креативы (${b.creatives})`}
        </button>
      </div>

      {search && (
        <div style={{ marginTop: 8 }}>
          <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="поиск бренда (мин. 2)…"
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)' }} />
          {(searchQ.data ?? []).map((h) => (
            <div key={h.brand_id} onClick={() => !busy && link(b.ext_ad_ids, h.brand_id)}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 'var(--fs-small)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              → {h.brand_name} <span style={{ opacity: 0.5 }}>({h.matched_via})</span>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 10, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {creativesQ.isLoading && <Skeleton height={80} />}
          {subgroups.map((sg) => (
            <SubGroup key={sg.key} sg={sg} busy={busy}
              onCreate={() => openCreate(sg.ids, sg.brand, sg.domain)} onSkip={() => skip(sg.ids)}
              onCreateOne={(c) => openCreate([c.ext_ad_id], c.visible_brand, c.display_domain)} onSkipOne={(c) => skip([c.ext_ad_id])}
              onZoom={setZoom} />
          ))}
        </div>
      )}

      {createT && (
        <div onClick={() => !busy && setCreateT(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 61, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: '100vw', maxHeight: '90vh', overflow: 'auto', background: '#0a1226', border: '1px solid rgba(15,45,100,0.6)', borderRadius: 10, padding: 18 }}>
            <div style={{ fontWeight: 700, marginBottom: 12 }}>Привязать {createT.ids.length} креатив.</div>

            {/* ── Link to an EXISTING brand (search) — the preferred action ── */}
            <label style={{ fontSize: 'var(--fs-small)', opacity: 0.7 }}>Найти существующий бренд</label>
            <input autoFocus value={mTerm} onChange={(e) => setMTerm(e.target.value)} placeholder="поиск бренда (мин. 2)…"
              style={{ width: '100%', margin: '4px 0 4px', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)' }} />
            {mDebounced.trim().length >= 2 && (
              <div style={{ maxHeight: 180, overflow: 'auto', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6, marginBottom: 10 }}>
                {mSearchQ.isLoading && <div style={{ padding: '6px 10px', fontSize: 'var(--fs-micro)', opacity: 0.5 }}>поиск…</div>}
                {!mSearchQ.isLoading && (mSearchQ.data ?? []).length === 0 && (
                  <div style={{ padding: '6px 10px', fontSize: 'var(--fs-micro)', opacity: 0.5 }}>ничего не найдено — создайте новый ниже</div>
                )}
                {(mSearchQ.data ?? []).map((h) => (
                  <div key={h.brand_id} onClick={() => !busy && link(createT.ids, h.brand_id)}
                    style={{ padding: '7px 10px', cursor: busy ? 'wait' : 'pointer', fontSize: 'var(--fs-small)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    → {h.brand_name} <span style={{ opacity: 0.5 }}>({h.matched_via})</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0', opacity: 0.5, fontSize: 'var(--fs-micro)' }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.12)' }} /> или создать новый <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.12)' }} />
            </div>

            <label style={{ fontSize: 'var(--fs-small)', opacity: 0.7 }}>Название бренда</label>
            <input value={createT.name} onChange={(e) => setCreateT({ ...createT, name: e.target.value })}
              style={{ width: '100%', margin: '4px 0 8px', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)' }} />
            <div style={{ fontSize: 'var(--fs-small)', opacity: 0.6, marginBottom: 12 }}>сайт: {createT.domain || '—'}</div>
            <label style={{ fontSize: 'var(--fs-small)', opacity: 0.7 }}>Подкатегория</label>
            <select value={subId} onChange={(e) => setSubId(e.target.value)}
              style={{ width: '100%', margin: '4px 0 6px', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)' }}>
              <option value="">— без подкатегории (на ревью) —</option>
              {subcats.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div style={{ fontSize: 'var(--fs-micro)', opacity: 0.5, marginBottom: 12 }}>Без подкатегории бренд создастся с флагом «на ревью».</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button disabled={busy} onClick={() => setCreateT(null)} style={btn('transparent', '#9aa', busy)}>Отмена</button>
              <button disabled={busy || !createT.name.trim()} onClick={doCreate} style={btn('#2ecc71', '#062', busy)}>Создать и привязать</button>
            </div>
          </div>
        </div>
      )}

      {zoom && (
        <div onClick={() => setZoom(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, maxHeight: '90vh', overflow: 'auto', background: '#0a1226', border: '1px solid rgba(15,45,100,0.6)', borderRadius: 10, padding: 16 }}>
            {imgOf(zoom.image_url)
              ? <img src={imgOf(zoom.image_url)!} alt="" style={{ maxWidth: '100%', borderRadius: 6 }} />
              : <div style={{ padding: 40, textAlign: 'center', color: '#5c6b86' }}>нет изображения (текстовое объявление)</div>}
            <div style={{ marginTop: 10, fontSize: 'var(--fs-small)' }}>
              {zoom.visible_brand && <b>{zoom.visible_brand}</b>}{zoom.display_domain ? ` · ${zoom.display_domain}` : ''}
              {zoom.ad_text && <div style={{ color: 'var(--muted)', marginTop: 4 }}>{zoom.ad_text}</div>}
              {zoom.landing_url && <div style={{ color: 'var(--muted)', marginTop: 4, wordBreak: 'break-all' }}>{zoom.landing_url}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubGroup({ sg, busy, onCreate, onSkip, onCreateOne, onSkipOne, onZoom }: {
  sg: { key: string; label: string; brand: string | null; domain: string | null; cs: Creative[]; ids: string[] };
  busy: boolean; onCreate: () => void; onSkip: () => void;
  onCreateOne: (c: Creative) => void; onSkipOne: (c: Creative) => void; onZoom: (c: Creative) => void;
}) {
  const [open, setOpen] = useState(sg.cs.length <= 3);
  return (
    <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6, padding: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setOpen(!open)} style={btn('transparent', '#bcd0ff', busy)}>{open ? '▾' : '▸'}</button>
        <b style={{ fontSize: 'var(--fs-small)' }}>{sg.label}</b>
        <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-micro)' }}>{sg.cs.length} шт{sg.domain ? ` · ${sg.domain}` : ''}</span>
        {sg.brand && <button disabled={busy} onClick={onCreate} style={btn('rgba(46,204,113,0.2)', '#7ee2a8', busy)}>+ «{sg.brand}» → {sg.cs.length}</button>}
        <button disabled={busy} onClick={onSkip} style={btn('transparent', '#9aa', busy)}>не бренд</button>
      </div>
      {open && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {sg.cs.map((c) => {
            const src = imgOf(c.image_url);
            return (
            <div key={c.ext_ad_id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '4px 0' }}>
              <div onClick={() => onZoom(c)} title="увеличить" style={{ width: 56, height: 42, flexShrink: 0, borderRadius: 4, cursor: 'pointer',
                background: src ? `center/contain no-repeat url(${src}), #0c1424` : '#0c1424',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5c6b86', fontSize: 14 }}>
                {!src && fmtIcon(c.format)}
                {src && c.format === 'video' && <span style={{ fontSize: 16 }}>▶</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--fs-micro)' }}>
                  {c.format && <span style={{ opacity: 0.7 }}>{fmtIcon(c.format)} </span>}
                  {c.visible_brand ? <b>{c.visible_brand}</b> : <span style={{ opacity: 0.5 }}>—</span>}
                  {c.display_domain && <span style={{ opacity: 0.6 }}> · {c.display_domain}</span>}
                </div>
                {c.ad_text && <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>«{c.ad_text}»</div>}
              </div>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {c.visible_brand && <button disabled={busy} onClick={() => onCreateOne(c)} style={btn('rgba(46,204,113,0.15)', '#7ee2a8', busy)}>+</button>}
                <button disabled={busy} onClick={() => onSkipOne(c)} style={btn('transparent', '#9aa', busy)}>✕</button>
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}
